import React, { useEffect, useState } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import {
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  Table,
  TableContainer,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import HighlightOffTwoToneIcon from "@mui/icons-material/HighlightOffTwoTone";
import { GridGenerator } from "@actinc/dls/components";
import {
  ControlledFormInput,
  ControlledFormSelect,
  ControlledFormSwitch,
} from "../../../../components";
import { v4 as uuidv4 } from "uuid";

const MatchTableInteractionForm = ({
  index,
  interaction,
  readonly,
  update,
}) => {
  /**
  console.debug(
    "MatchTableInteractionForm - index %s, interaction %o",
    index,
    interaction,
  );
  */

  const namePrefix = `item.interactions[${index}]`;

  const { control, getValues, setValue } = useFormContext();

  const {
    append: appendColumnChoice,
    fields: columnChoices,
    remove: removeColumnChoice,
    replace: replaceColumnChoice,
  } = useFieldArray({
    control,
    name: `${namePrefix}.columnChoices`,
  });

  const {
    append: appendRowChoice,
    fields: rowChoices,
    remove: removeRowChoice,
    replace: replaceRowChoice,
  } = useFieldArray({
    control,
    name: `${namePrefix}.rowChoices`,
  });

  const [currentTemplate, setCurrentTemplate] = useState({});

  const constructOptions = [
    {
      label: "Collaboration",
      value: "Collaboration",
    },
    {
      label: "Creativity",
      value: "Creativity",
    },
    {
      label: "Integrity",
      value: "Integrity",
    },
    {
      label: "Leadership",
      value: "Leadership",
    },
    {
      label: "Resilience",
      value: "Resilience",
    },
    {
      label: "Work Ethics",
      value: "Work Ethics",
    },
  ];

  const applyTemplate = (templateName) => {
    if (templateName && templates[templateName]) {
      console.log("applyTemplate", templates[templateName]);
      replaceColumnChoice(
        templates[templateName].columns.map((el, idx) => ({
          content: el,
          identifier: uuidv4(),
          matchMax: "0",
          matchMaxLabel: "Match Max",
          name: `CN${idx + 1}`,
        })),
      );
      replaceRowChoice(
        templates[templateName].rows.map((el, idx) => ({
          content: el,
          identifier: uuidv4(),
          matchMax: 1,
          matchMaxLabel: "Match Max",
          constructLabel: "Construct",
          name: `RN${idx + 1}`,
        })),
      );
      setCurrentTemplate(templates[templateName]);
      setValue(`${namePrefix}.rowCentric`, templates[templateName].rowCentric);
    }
  };

  useEffect(() => {
    if (!columnChoices.length && !rowChoices.length) {
      applyTemplate(interaction.templateName);
    }
  }, [columnChoices, rowChoices]);

  const addColumnChoice = () => {
    appendColumnChoice({
      content: "Text",
      identifier: uuidv4(),
      matchMax: 0,
      matchMaxLabel: "Match Max",
      name: `CN${columnChoices.length + 1}`,
    });
  };
  const deleteColumnChoice = (i) => () => {
    removeColumnChoice(i);
  };

  const addRowChoice = () => {
    appendRowChoice({
      content: "Text",
      identifier: uuidv4(),
      matchMaxLabel: "Match Max",
      constructLabel: "Construct",
      name: `RN${rowChoices.length + 1}`,
    });
  };
  const deleteRowChoice = (i) => () => {
    removeRowChoice(i);
  };

  if (!getValues(`${namePrefix}.identifier`)) return null;

  return (
    <Box
      sx={{
        alignItems: "flex-start",
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        gap: 4,
        width: "100%",
      }}
    >
      {/* Match Table Box */}
      <Card sx={{ width: "100%" }}>
        <CardContent>
          <GridGenerator
            groups={[
              [
                {
                  children: (
                    <ControlledFormSelect
                      control={control}
                      defaultValue={interaction.templateName}
                      fullWidth={false}
                      id={`${namePrefix}.templateName`}
                      label="Template"
                      name={`${namePrefix}.templateName`}
                      onChange={(e) => applyTemplate(e.target.value)}
                      options={templateOptions}
                      readonly={readonly}
                    />
                  ),
                  key: `${namePrefix}.templateName`,
                },
                {
                  children: (
                    <ControlledFormSwitch
                      control={control}
                      defaultValue={interaction.rowCentric}
                      id={`${namePrefix}.rowCentric`}
                      label="Row Centric"
                      name={`${namePrefix}.rowCentric`}
                      readonly={readonly}
                    />
                  ),
                  key: `${namePrefix}.rowCentric`,
                },
              ],
              [
                {
                  children: (
                    <ControlledFormInput
                      control={control}
                      defaultValue={interaction.prompt}
                      id={`${namePrefix}.prompt`}
                      label="Prompt Text"
                      multiline={true}
                      name={`${namePrefix}.prompt`}
                      readonly={readonly}
                    />
                  ),
                  key: `${namePrefix}.prompt`,
                },
              ],
            ]}
          />
        </CardContent>
      </Card>

      {/* Column Box */}
      <Card sx={{ width: "100%" }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Columns
          </Typography>
          <TableContainer sx={{ width: "100%", overflowX: "auto" }}>
            <Table
              size="small"
              sx={{ minWidth: 650 }}
              aria-label="simple table"
            >
              <TableBody>
                {!!columnChoices.length &&
                  columnChoices.map((columnChoice, idx) => (
                    <TableRow key={columnChoice.identifier}>
                      <TableCell sx={{ verticalAlign: "top", minWidth: 140 }}>
                        <ControlledFormInput
                          control={control}
                          defaultValue={columnChoice.name || `CN${idx + 1}`}
                          id={`${namePrefix}.columnChoices[${idx}].name`}
                          label={`Column ${idx + 1} Name`}
                          name={`${namePrefix}.columnChoices[${idx}].name`}
                          readonly={readonly}
                          rules={{ required: true }}
                        />
                      </TableCell>
                      <TableCell sx={{ minWidth: 260 }}>
                        <ControlledFormInput
                          control={control}
                          defaultValue={columnChoice.content}
                          id={`${namePrefix}.columnChoices[${idx}].content`}
                          label={`Column ${idx + 1} Text`}
                          multiline={true}
                          name={`${namePrefix}.columnChoices[${idx}].content`}
                          readonly={readonly}
                          rules={{
                            required: true,
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ verticalAlign: "top", minWidth: 120 }}>
                        <ControlledFormSelect
                          control={control}
                          defaultValue={columnChoice.matchMax}
                          id={`${namePrefix}.columnChoices[${idx}].matchMax`}
                          label="Match Max"
                          name={`${namePrefix}.columnChoices[${idx}].matchMax`}
                          options={matchMaxOptions}
                          readonly={readonly}
                          rules={{
                            required: true,
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ verticalAlign: "top", width: 60 }}>
                        {currentTemplate.minColumns <= idx && (
                          <IconButton onClick={deleteColumnChoice(idx)}>
                            <HighlightOffTwoToneIcon />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Box mt={2}>
            <Button
              variant="contained"
              onClick={addColumnChoice}
              disabled={readonly}
            >
              Add
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Rows */}
      <Card sx={{ width: "100%" }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Rows
          </Typography>
          <TableContainer sx={{ width: "100%", overflowX: "auto" }}>
            <Table size="small" sx={{ minWidth: 650 }} aria-label="rows table">
              <TableBody>
                {!!rowChoices.length &&
                  rowChoices.map((rowChoice, idx) => (
                    <TableRow key={rowChoice.identifier}>
                      <TableCell sx={{ verticalAlign: "top", minWidth: 140 }}>
                        <ControlledFormInput
                          control={control}
                          defaultValue={rowChoice.name || `RN${idx + 1}`}
                          id={`${namePrefix}.rowChoices[${idx}].name`}
                          label={`Row ${idx + 1} Name`}
                          name={`${namePrefix}.rowChoices[${idx}].name`}
                          readonly={readonly}
                          rules={{ required: true }}
                        />
                      </TableCell>
                      <TableCell sx={{ minWidth: 260 }}>
                        <ControlledFormInput
                          control={control}
                          defaultValue={rowChoice.content}
                          id={`${namePrefix}.rowChoices[${idx}].content`}
                          label={`Row ${idx + 1} Text`}
                          multiline
                          name={`${namePrefix}.rowChoices[${idx}].content`}
                          readonly={readonly}
                          rules={{ required: true }}
                        />
                      </TableCell>
                      <TableCell sx={{ verticalAlign: "top", minWidth: 120 }}>
                        <ControlledFormSelect
                          control={control}
                          defaultValue={rowChoice.matchMax || "1"}
                          id={`${namePrefix}.rowChoices[${idx}].matchMax`}
                          label="Match Max"
                          name={`${namePrefix}.rowChoices[${idx}].matchMax`}
                          options={matchMaxOptions}
                          readonly={readonly}
                          rules={{ required: true }}
                        />
                      </TableCell>
                      <TableCell sx={{ verticalAlign: "top", minWidth: 160 }}>
                        <ControlledFormSelect
                          control={control}
                          defaultValue={rowChoice.construct}
                          id={`${namePrefix}.rowChoices[${idx}].construct`}
                          label="Construct"
                          name={`${namePrefix}.rowChoices[${idx}].construct`}
                          options={constructOptions}
                          readonly={readonly}
                          rules={{ required: true }}
                        />
                      </TableCell>
                      <TableCell sx={{ verticalAlign: "top", width: 60 }}>
                        {idx !== 0 && (
                          <IconButton
                            onClick={deleteRowChoice(idx)}
                            size="small"
                          >
                            <HighlightOffTwoToneIcon fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Box mt={2}>
            <Button
              variant="contained"
              onClick={addRowChoice}
              disabled={readonly}
            >
              Add
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

const defaultTemplate = {
  columns: [...Array(5)].map((el, idx) => `Text`),
  label: "default",
  minColumns: 1,
  minRows: 1,
  rows: ["Text"],
  rowCentric: true,
};

const forcedChoiceTemplate = {
  columns: ["Most Like Me", "In Between", "Least Like Me"],
  label: "Forced Choice",
  minColumns: 3,
  minRows: 1,
  rows: ["Text"],
  rowCentric: false,
};

const matchMaxOptions = [...Array(10)].map((el, idx) => ({
  label: idx,
  value: idx,
}));

const matrixTemplate = {
  columns: [
    "Strongly Disagree",
    "Disagree",
    "Somewhat Disagree",
    "Somewhat Agree",
    "Agree",
    "Strongly Agree",
  ],
  label: "Matrix",
  minColumns: 6,
  minRows: 1,
  rows: ["Text"],
  rowCentric: true,
};

const situationalJudgementTemplate = {
  columns: [
    "Very Unlikely",
    "Unlikely",
    "May or May Not",
    "Likely",
    "Very Likely",
  ],
  label: "Situational Judgement",
  minColumns: 5,
  minRows: 1,
  rows: ["Text"],
  rowCentric: true,
};

const templates = {
  forcedChoiceTemplate,
  matrixTemplate,
  situationalJudgementTemplate,
  defaultTemplate,
};

const templateOptions = Object.entries(templates).map(
  ([key, { label, minColumns }]) => {
    return { label, minColumns, value: key };
  },
);

export { MatchTableInteractionForm };
